// BL-44 — the audit log, written for operators.
//
// It used to print the raw row: `cash_remittance.handed_over`, a bare actor
// uuid, and `{"a":1} → {"b":2}` truncated to a tooltip. Every column now says
// who did what to which record, and a row expands into field-level changes.
//
// ⚠ PRESENTATION ONLY. `audit_log` is append-only (0012) and "clean the log"
// means readable, not shorter (intake correction A-2). Nothing here deletes,
// edits or hides a row — the filters narrow a query, they do not curate.
//
// Superadmin only, unchanged: 0012 grants SELECT to that role alone, and
// Luigi's Dashboard Access standard puts history logs there too. The backlog
// board still says "hub-scoped for hub managers"; Leon settled it the other way
// on 2026-09-26 and again on 2026-10-04.

import { PageHeader } from "@bednbody/ui/components/page-header";
import { AdminDataTable, AdminTableRowMenu, type AdminTableFacet } from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import {
  type AuditGroup,
  auditChanges,
  describeAuditAction,
  parseAuditEntity,
} from "./audit.describe";
import { type AuditEntry, useAuditActions, useAuditLog, useAuditNames } from "./audit.hooks";

const GROUP_LABEL: Record<AuditGroup, string> = {
  money: "Money",
  people: "People",
  bookings: "Bookings",
  sessions: "Sessions",
  safety: "Safety",
  settings: "Settings",
  other: "Other",
};

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });

export function AuditPage() {
  const [filters, setFilters] = useState({ action: "", dateFrom: "", dateTo: "" });
  const { data: auditLog, isLoading } = useAuditLog(filters);
  const { data: actions } = useAuditActions();
  const entries = useMemo(() => auditLog ?? [], [auditLog]);
  const names = useAuditNames(entries);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const actorOf = useCallback(
    (entry: AuditEntry) => entry.actor_id ? (names.actors.get(entry.actor_id) ?? null) : null,
    [names.actors],
  );

  const recordOf = useCallback((entry: AuditEntry) => {
    const { table, id } = parseAuditEntity(entry.entity);
    const label = id ? names.records.get(id) : null;
    return { table: table.replaceAll("_", " "), label, id };
  }, [names.records]);

  // Expanding a change only updates presentation. Keep the filtering inputs
  // stable so TanStack does not treat that render as a new data set.
  const facets = useMemo<readonly AdminTableFacet<AuditEntry>[]>(() => [
    {
      id: "action",
      label: "Action",
      options: (actions ?? []).map((action) => ({
        value: action,
        label: describeAuditAction(action).label,
      })),
      getValue: (entry) => entry.action,
    },
  ], [actions]);
  const searchText = useCallback((entry: AuditEntry) => [
    describeAuditAction(entry.action).label,
    entry.action,
    actorOf(entry)?.name,
    recordOf(entry).label,
    entry.entity,
    entry.id,
  ].join(" "), [actorOf, recordOf]);

  const columns: ColumnDef<AuditEntry, unknown>[] = [
    {
      id: "created",
      accessorFn: (entry) => entry.created_at,
      header: "When",
      cell: ({ row }) => <span className="whitespace-nowrap">{when(row.original.created_at)}</span>,
    },
    {
      id: "actor",
      accessorFn: (entry) => actorOf(entry)?.name ?? "",
      header: "Who",
      cell: ({ row }) => {
        const actor = actorOf(row.original);
        if (!actor) {
          // No actor is not a gap: the nightly jobs and triggers write rows too.
          return <span className="text-muted-foreground">Bednbody system</span>;
        }
        return (
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{actor.name}</p>
            <p className="truncate text-xs capitalize text-muted-foreground">
              {actor.role.replaceAll("_", " ")}
            </p>
          </div>
        );
      },
    },
    {
      id: "what",
      accessorFn: (entry) => describeAuditAction(entry.action).label,
      header: "What happened",
      cell: ({ row }) => {
        const described = describeAuditAction(row.original.action);
        return (
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-foreground">{described.label}</span>
            <Badge className="shrink-0" shape="soft" variant="muted-soft">
              {GROUP_LABEL[described.group]}
            </Badge>
          </div>
        );
      },
    },
    {
      id: "record",
      accessorFn: (entry) => {
        const record = recordOf(entry);
        return record.label ?? record.table;
      },
      header: "Record",
      cell: ({ row }) => {
        const record = recordOf(row.original);
        return (
          <div className="min-w-0">
            <p className="truncate capitalize text-foreground">{record.label ?? record.table}</p>
            {record.label ? (
              <p className="truncate text-xs capitalize text-muted-foreground">{record.table}</p>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "changes",
      header: "Changes",
      enableSorting: false,
      cell: ({ row }) => {
        const changes = auditChanges(row.original.before, row.original.after);
        if (changes.length === 0) return <span className="text-muted-foreground">—</span>;
        const open = expanded.has(row.original.id);
        return (
          <Button
            onClick={() =>
              setExpanded((current) => {
                const next = new Set(current);
                if (next.has(row.original.id)) next.delete(row.original.id);
                else next.add(row.original.id);
                return next;
              })
            }
            shape="soft"
            size="sm"
            variant="ghost"
          >
            {changes.length} {changes.length === 1 ? "field" : "fields"}
            {open ? (
              <CaretUpIcon data-icon="inline-end" />
            ) : (
              <CaretDownIcon data-icon="inline-end" />
            )}
          </Button>
        );
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          actions={[
            {
              label: "Copy event ID",
              onSelect: () => void navigator.clipboard.writeText(row.original.id),
            },
            {
              label: "Copy record ID",
              onSelect: () => {
                const { id } = parseAuditEntity(row.original.entity);
                if (id) void navigator.clipboard.writeText(id);
              },
            },
          ]}
          label="audit event"
        />
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <PageHeader
        description="Who did what, and when. Entries can never be edited or removed."
        title="Audit log"
      />

      <AdminDataTable
        presentation="standalone"
        columns={columns}
        data={entries}
        emptyDescription="Nothing matches these filters."
        emptyTitle="No events"
        exportColumns={[
          { header: "When", value: (entry) => entry.created_at },
          { header: "Who", value: (entry) => actorOf(entry)?.name ?? "Bednbody system" },
          { header: "Role", value: (entry) => actorOf(entry)?.role ?? "" },
          { header: "What happened", value: (entry) => describeAuditAction(entry.action).label },
          { header: "Action code", value: (entry) => entry.action },
          { header: "Record", value: (entry) => recordOf(entry).label ?? recordOf(entry).table },
          { header: "Record ID", value: (entry) => parseAuditEntity(entry.entity).id ?? "" },
          {
            header: "Changes",
            value: (entry) =>
              auditChanges(entry.before, entry.after)
                .map((change) => `${change.field}: ${change.from} → ${change.to}`)
                .join("; "),
          },
        ]}
        exportFilename="audit-log"
        exportScopeLabel="the events matching these filters"
        facets={facets}
        getRowId={(entry) => entry.id}
        header={
          <div className="flex flex-wrap items-center gap-2">
            <Label className="type-body-default text-muted-foreground" htmlFor="audit-from">
              From
            </Label>
            <Input
              className="w-40"
              id="audit-from"
              max={filters.dateTo || undefined}
              onChange={(event) =>
                setFilters((current) => ({ ...current, dateFrom: event.target.value }))
              }
              type="date"
              value={filters.dateFrom}
            />
            <Label className="type-body-default text-muted-foreground" htmlFor="audit-to">
              to
            </Label>
            <Input
              className="w-40"
              id="audit-to"
              min={filters.dateFrom || undefined}
              onChange={(event) =>
                setFilters((current) => ({ ...current, dateTo: event.target.value }))
              }
              type="date"
              value={filters.dateTo}
            />
            {filters.dateFrom || filters.dateTo ? (
              <Button
                onClick={() => setFilters((current) => ({ ...current, dateFrom: "", dateTo: "" }))}
                shape="soft"
                size="sm"
                variant="ghost"
              >
                Clear dates
              </Button>
            ) : null}
          </div>
        }
        initialSorting={[{ id: "created", desc: true }]}
        isRowExpanded={(entry) => expanded.has(entry.id)}
        loading={isLoading}
        renderExpandedRow={(entry) => {
          const changes = auditChanges(entry.before, entry.after);
          return (
            <dl className="divide-y divide-border px-4 py-2 text-sm">
              {changes.map((change) => (
                <div className="flex flex-wrap items-baseline gap-2 py-2" key={change.field}>
                  <dt className="w-48 shrink-0 capitalize text-muted-foreground">{change.field}</dt>
                  <dd className="flex min-w-0 flex-1 flex-wrap items-baseline gap-2">
                    <span className="text-muted-foreground line-through">{change.from}</span>
                    <span aria-hidden>→</span>
                    <span className="font-medium text-foreground">{change.to}</span>
                  </dd>
                </div>
              ))}
            </dl>
          );
        }}
        searchPlaceholder="Search what happened, who, or a record…"
        searchText={searchText}
      />
    </div>
  );
}
